import type { AgentIntentDiagnosis } from "@/lib/agent-diagnosis";
import type { AgentMemoryEvent } from "@/lib/agent-memory-types";
import type { SEOIntentConfig, SEOOfferType } from "@/lib/seo-intents";

export const AGENT_OFFER_LEARNED_PRIORS_STORAGE_KEY = "ishvaram_agent_offer_learned_priors_v1";

export interface AgentOfferPrior {
  shown?: number;
  clicked?: number;
  checkoutStarted?: number;
  checkoutAbandoned?: number;
  dismissed?: number;
  purchased?: number;
  revenueInr?: number;
  scoreBoost?: number;
}

export type AgentOfferPriors = Record<string, AgentOfferPrior>;
type PriorCountField = Exclude<keyof AgentOfferPrior, "scoreBoost">;

const PRIOR_COUNT_FIELDS: PriorCountField[] = [
  "shown",
  "clicked",
  "checkoutStarted",
  "checkoutAbandoned",
  "dismissed",
  "purchased",
  "revenueInr",
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function numberOrUndefined(value: unknown) {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function normalizePrior(value: unknown): AgentOfferPrior | null {
  if (!isRecord(value)) return null;
  const prior: AgentOfferPrior = {};
  for (const field of PRIOR_COUNT_FIELDS) {
    assignNonNegativePrior(prior, field, numberOrUndefined(value[field]));
  }
  assignScoreBoost(prior, numberOrUndefined(value.scoreBoost));

  return Object.keys(prior).length > 0 ? prior : null;
}

function assignNonNegativePrior(
  prior: AgentOfferPrior,
  field: PriorCountField,
  value: number | undefined
) {
  if (value === undefined) return;
  prior[field] = Math.max(0, value);
}

function assignScoreBoost(prior: AgentOfferPrior, value: number | undefined) {
  if (value === undefined) return;
  prior.scoreBoost = clamp(value, -30, 30);
}

export function normalizeAgentOfferPriors(value: unknown): AgentOfferPriors {
  const parsed =
    typeof value === "string"
      ? (() => {
          try {
            return JSON.parse(value) as unknown;
          } catch {
            return null;
          }
        })()
      : value;

  if (!isRecord(parsed)) return {};

  const normalized: AgentOfferPriors = {};
  for (const [key, priorValue] of Object.entries(parsed)) {
    const prior = normalizePrior(priorValue);
    if (prior) normalized[key] = prior;
  }
  return normalized;
}

function mergePrior(existing: AgentOfferPrior | undefined, incoming: AgentOfferPrior) {
  const merged: AgentOfferPrior = { ...(existing ?? {}) };
  for (const field of PRIOR_COUNT_FIELDS) {
    addPriorField(merged, incoming, field);
  }
  if (incoming.scoreBoost !== undefined) merged.scoreBoost = incoming.scoreBoost;
  return merged;
}

function addPriorField(target: AgentOfferPrior, incoming: AgentOfferPrior, field: PriorCountField) {
  if (incoming[field] === undefined) return;
  target[field] = (target[field] ?? 0) + incoming[field];
}

export function buildAgentOfferPriorKeys({
  config,
  diagnosis,
  offerType,
}: {
  config: SEOIntentConfig;
  diagnosis?: AgentIntentDiagnosis;
  offerType: SEOOfferType;
}) {
  const problem = diagnosis?.customerProblem || config.pageCategory;
  return [
    `${config.intent}:${problem}:${offerType}`,
    `${config.intent}:*:${offerType}`,
    `*:${problem}:${offerType}`,
    `*:*:${offerType}`,
  ];
}

export function buildAgentOfferPriorKeysFromContext({
  seoIntent,
  customerProblem,
  offerType,
}: {
  seoIntent?: string;
  customerProblem?: string;
  offerType: string;
}) {
  const intent = sanitizePriorKeyPart(seoIntent) || "*";
  const problem = sanitizePriorKeyPart(customerProblem) || "*";
  const offer = sanitizePriorKeyPart(offerType);
  if (!offer) return [];
  return uniqueKeys([
    `${intent}:${problem}:${offer}`,
    `${intent}:*:${offer}`,
    `*:${problem}:${offer}`,
    `*:*:${offer}`,
  ]);
}

function sanitizePriorKeyPart(value: string | undefined) {
  if (!value) return "";
  return value.trim().replaceAll(":", "_");
}

function uniqueKeys(keys: string[]) {
  return Array.from(new Set(keys));
}

export function getAgentOfferPriorBoost({
  priors,
  config,
  diagnosis,
  offerType,
}: {
  priors?: AgentOfferPriors;
  config: SEOIntentConfig;
  diagnosis?: AgentIntentDiagnosis;
  offerType: SEOOfferType;
}) {
  if (!priors) return 0;
  for (const key of buildAgentOfferPriorKeys({ config, diagnosis, offerType })) {
    const boost = boostForPrior(priors[key]);
    if (boost !== null) return boost;
  }
  return 0;
}

function boostForPrior(prior: AgentOfferPrior | undefined): number | null {
  if (!prior) return null;
  if (prior.scoreBoost !== undefined) return prior.scoreBoost;

  const metrics = priorMetrics(prior);
  if (!hasEnoughSignal(metrics)) return null;

  return Math.round(clamp(rawBoost(metrics) * confidenceWeight(metrics.shown), -20, 30));
}

function priorMetrics(prior: AgentOfferPrior) {
  return {
    shown: prior.shown ?? 0,
    clicked: prior.clicked ?? 0,
    checkoutStarted: prior.checkoutStarted ?? 0,
    purchased: prior.purchased ?? 0,
    revenueInr: prior.revenueInr ?? 0,
    dismissed: prior.dismissed ?? 0,
    checkoutAbandoned: prior.checkoutAbandoned ?? 0,
  };
}

function rawBoost(metrics: ReturnType<typeof priorMetrics>) {
  const denominator = Math.max(metrics.shown, 1);
  return (
    (metrics.purchased / denominator) * 120 +
    (metrics.checkoutStarted / denominator) * 25 +
    (metrics.clicked / denominator) * 12 +
    Math.min(metrics.revenueInr / denominator / 15, 10) -
    (metrics.dismissed / denominator) * 10 -
    (metrics.checkoutAbandoned / denominator) * 8
  );
}

function confidenceWeight(shown: number) {
  return clamp(Math.log10(Math.max(shown, 10)) / 3, 0.35, 1);
}

function hasEnoughSignal({
  shown,
  clicked,
  checkoutStarted,
  purchased,
}: {
  shown: number;
  clicked: number;
  checkoutStarted: number;
  purchased: number;
}) {
  return shown >= 20 || purchased > 0 || checkoutStarted >= 3 || clicked >= 5;
}

function readPriorsFromStorage(storageKey: string): AgentOfferPriors {
  if (typeof window === "undefined") return {};
  try {
    return normalizeAgentOfferPriors(window.localStorage.getItem(storageKey));
  } catch {
    return {};
  }
}

function writePriorsToStorage(storageKey: string, value: unknown) {
  if (typeof window === "undefined") return;
  const priors = normalizeAgentOfferPriors(value);
  try {
    if (Object.keys(priors).length === 0) {
      window.localStorage.removeItem(storageKey);
      return;
    }
    window.localStorage.setItem(storageKey, JSON.stringify(priors));
  } catch {
    // Ranking falls back to static priors when storage is unavailable.
  }
}

export function loadAgentOfferPriors(): AgentOfferPriors {
  return loadLearnedAgentOfferPriors();
}

export function loadLearnedAgentOfferPriors(): AgentOfferPriors {
  return readPriorsFromStorage(AGENT_OFFER_LEARNED_PRIORS_STORAGE_KEY);
}

function saveLearnedAgentOfferPriors(value: unknown) {
  writePriorsToStorage(AGENT_OFFER_LEARNED_PRIORS_STORAGE_KEY, value);
}

export function recordAgentOfferPriorEvent(event: AgentMemoryEvent) {
  const delta = priorDeltaForEvent(event);
  if (!delta) return;

  const keys = buildAgentOfferPriorKeysFromContext({
    seoIntent: event.seoIntent,
    customerProblem: event.customerProblem,
    offerType: event.offerType ?? "",
  });
  if (keys.length === 0) return;

  const priors = loadLearnedAgentOfferPriors();
  for (const key of keys) {
    priors[key] = mergePrior(priors[key], delta);
  }
  saveLearnedAgentOfferPriors(priors);
}

function priorDeltaForEvent(event: AgentMemoryEvent): AgentOfferPrior | null {
  switch (event.type) {
    case "offer_shown":
      return { shown: 1 };
    case "offer_clicked":
      return { clicked: 1 };
    case "checkout_started":
      return { checkoutStarted: 1 };
    case "checkout_abandoned":
      return { checkoutAbandoned: 1 };
    case "offer_dismissed":
      return { dismissed: 1 };
    case "purchase_completed":
      return { purchased: 1, revenueInr: event.priceInr ?? 0 };
    default:
      return null;
  }
}
